import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Plus, Save, Heart, Thermometer, Activity, Wind, Droplet, Gauge } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceArea } from 'recharts';
import toast from 'react-hot-toast';
import careMonitorService from '../services/careMonitorService';

const PatientVitalsModal = ({ patient, onClose }) => {
  const [selectedVital, setSelectedVital] = useState('Temperature');
  const [vitalsData, setVitalsData] = useState({});
  const [notesData, setNotesData] = useState({});
  const [currentDateIndex, setCurrentDateIndex] = useState(0);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch REAL patient vitals data from backend
  useEffect(() => {
    const fetchAndTransformPatientVitals = async () => {
      try {
        setLoading(true);
        
        // Get patient numeric ID
        const numericId = patient.id;
        
        if (!numericId) {
          console.error("❌ Patient numeric ID is missing:", patient);
          toast.error("Patient ID missing. Cannot load vitals.");
          setLoading(false);
          return;
        }
        
        console.log(`🔍 Fetching vitals for patient ${patient.patient_id} (ID: ${numericId})`);
        
        // Fetch patient vitals from backend (fetch 7 days to show historical data)
        const backendData = await careMonitorService.getPatientVitalsDetail(numericId, '7days');
        
        console.log(`✅ Received backend data:`, backendData);
        
        // Transform backend data to match original modal's expected structure
        const transformedVitalsData = {};
        const transformedNotesData = {};
        
        // Helper function to determine vital status based on value
        const getVitalStatus = (vitalKey, value) => {
          if (value === null || value === undefined) return 'normal';
          
          const ranges = {
            temperature: { normal: [36.1, 37.2], warning: [35.5, 38.0], unit: '°C' },
            pulse_rate: { normal: [60, 100], warning: [50, 120], unit: 'bpm' },
            spo2: { normal: [95, 100], warning: [90, 95], unit: '%' },
            respiratory_rate: { normal: [12, 20], warning: [10, 25], unit: '/min' },
            glucose: { normal: [70, 140], warning: [60, 180], unit: 'mg/dL' }
          };
          
          const range = ranges[vitalKey];
          if (!range) return 'normal';
          
          if (value >= range.normal[0] && value <= range.normal[1]) return 'normal';
          if (value >= range.warning[0] && value <= range.warning[1]) return 'warning';
          return 'danger';
        };
        
        const getVitalUnit = (vitalKey) => {
          const units = {
            temperature: '°C',
            pulse_rate: 'bpm',
            spo2: '%',
            respiratory_rate: '/min',
            glucose: 'mg/dL'
          };
          return units[vitalKey] || '';
        };
        
        // Define vital types with their backend keys
        const vitalTypes = [
          { name: 'Temperature', key: 'temperature' },
          { name: 'Pulse Rate', key: 'pulse_rate' },
          { name: 'Blood Pressure', key: 'blood_pressure' },
          { name: 'SpO2', key: 'spo2' },
          { name: 'Resp. Rate', key: 'respiratory_rate' },
          { name: 'Glucose', key: 'glucose' }
        ];
        
        // FIRST PASS: Extract notes from all readings (notes are shared across all vitals for a date)
        // This ensures notes show for all vital types, not just the first one processed
        if (backendData.readings) {
          backendData.readings.forEach(reading => {
            const recordedDate = new Date(reading.recorded_at);
            const dateStr = recordedDate.toLocaleDateString('en-GB'); // '07/11/2025'
            
            // Extract notes (only save if not already present for this date)
            if (reading.additional_notes && !transformedNotesData[dateStr]) {
              transformedNotesData[dateStr] = {
                note: reading.additional_notes,
                author: reading.additional_notes_author || 'Admin',
                date: dateStr
              };
            }
          });
        }
        
        // SECOND PASS: Group readings by date for each vital type
        vitalTypes.forEach(vitalType => {
          const readingsByDate = {};
          
          // Process each reading from backend
          if (backendData.readings) {
            backendData.readings.forEach(reading => {
              // Extract date and time
              const recordedDate = new Date(reading.recorded_at);
              const dateStr = recordedDate.toLocaleDateString('en-GB'); // '07/11/2025'
              const timeStr = recordedDate.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }); // '08:15'
              
              // Get raw vital value from backend
              let rawValue = null;
              let displayValue = null;
              let unit = '';
              let status = 'normal';
              
              // Special handling for Blood Pressure (combine systolic and diastolic)
              if (vitalType.name === 'Blood Pressure') {
                const systolic = reading.systolic_bp;
                const diastolic = reading.diastolic_bp;
                
                console.log(`🩸 BP Debug - Systolic: ${systolic}, Diastolic: ${diastolic}`);
                
                // Show BP if we have at least one value
                if ((systolic !== null && systolic !== undefined) || (diastolic !== null && diastolic !== undefined)) {
                  const systolicVal = (systolic !== null && systolic !== undefined) ? systolic : '--';
                  const diastolicVal = (diastolic !== null && diastolic !== undefined) ? diastolic : '--';
                  displayValue = `${systolicVal}/${diastolicVal}`;
                  unit = 'mmHg';
                  
                  // Determine status based on systolic (primary indicator)
                  if (systolic !== null && systolic !== undefined && diastolic !== null && diastolic !== undefined) {
                    if (systolic >= 90 && systolic <= 120 && diastolic >= 60 && diastolic <= 80) {
                      status = 'normal';
                    } else if (systolic >= 120 && systolic <= 140 || diastolic >= 80 && diastolic <= 90) {
                      status = 'warning';
                    } else {
                      status = 'danger';
                    }
                  } else if (systolic !== null && systolic !== undefined) {
                    // Only systolic available
                    if (systolic >= 90 && systolic <= 120) {
                      status = 'normal';
                    } else if (systolic >= 120 && systolic <= 140) {
                      status = 'warning';
                    } else {
                      status = 'danger';
                    }
                  }
                }
              } else {
                // For other vitals, get raw value
                rawValue = reading[vitalType.key];
                if (rawValue !== null && rawValue !== undefined) {
                  displayValue = String(rawValue);
                  unit = getVitalUnit(vitalType.key);
                  status = getVitalStatus(vitalType.key, rawValue);
                }
              }
              
              if (displayValue !== null) {
                // Initialize date entry if not exists
                if (!readingsByDate[dateStr]) {
                  readingsByDate[dateStr] = {
                    date: dateStr,
                    readings: []
                  };
                }
                
                // Add reading to this date
                readingsByDate[dateStr].readings.push({
                  time: timeStr,
                  value: displayValue,
                  unit: unit,
                  status: status
                });
              }
            });
          }
          
          // Convert to array and sort by date
          const dateArray = Object.values(readingsByDate).sort((a, b) => {
            const dateA = a.date.split('/').reverse().join('');
            const dateB = b.date.split('/').reverse().join('');
            return dateA.localeCompare(dateB);
          });
          
          transformedVitalsData[vitalType.name] = dateArray;
        });
        
        console.log(`✅ Transformed vitals data:`, transformedVitalsData);
        console.log(`✅ Transformed notes data:`, transformedNotesData);
        
        // Log sample data for debugging
        if (transformedVitalsData['Temperature']?.length > 0) {
          console.log(`📊 Sample Temperature data:`, transformedVitalsData['Temperature'][0]);
        }
        if (transformedVitalsData['Blood Pressure']?.length > 0) {
          console.log(`📊 Sample BP data:`, transformedVitalsData['Blood Pressure'][0]);
        }
        
        setVitalsData(transformedVitalsData);
        setNotesData(transformedNotesData);
        
        // Set current date index to show today (last entries)
        const tempDataLength = transformedVitalsData['Temperature']?.length || 0;
        if (tempDataLength > 5) {
          setCurrentDateIndex(tempDataLength - 5);
        } else {
          setCurrentDateIndex(0);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('❌ Error fetching patient vitals:', error);
        toast.error('Failed to load patient vitals data');
        setLoading(false);
        
        // Set empty data to prevent errors
        setVitalsData({});
        setNotesData({});
      }
    };

    if (patient) {
      fetchAndTransformPatientVitals();
    }
  }, [patient]);

  const vitalsMenu = [
    { name: 'Temperature', icon: Thermometer, color: 'blue' },
    { name: 'Pulse Rate', icon: Heart, color: 'red' },
    { name: 'Blood Pressure', icon: Activity, color: 'purple' },
    { name: 'SpO2', icon: Droplet, color: 'cyan' },
    { name: 'Resp. Rate', icon: Wind, color: 'green' },
    { name: 'Glucose', icon: Gauge, color: 'orange' }
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case 'normal': return 'text-green-600 bg-green-50';
      case 'warning': return 'text-orange-600 bg-orange-50';
      case 'danger': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getChartColor = (vital) => {
    const colors = {
      'Temperature': '#3b82f6',
      'Pulse Rate': '#ef4444',
      'Blood Pressure': '#a855f7',
      'SpO2': '#06b6d4',
      'Resp. Rate': '#10b981',
      'Glucose': '#f59e0b'
    };
    return colors[vital] || '#3b82f6';
  };

  const getNormalRange = (vital) => {
    const ranges = {
      'Temperature': { min: 36.1, max: 37.2, unit: '°C' },
      'Pulse Rate': { min: 60, max: 100, unit: 'bpm' },
      'Blood Pressure': { min: 90, max: 120, unit: 'mmHg (Systolic)' },
      'SpO2': { min: 95, max: 100, unit: '%' },
      'Resp. Rate': { min: 12, max: 20, unit: '/min' },
      'Glucose': { min: 70, max: 140, unit: 'mg/dL' }
    };
    return ranges[vital];
  };

  const prepareChartData = () => {
    if (!vitalsData[selectedVital]) return [];
    
    const visibleDays = getVisibleDays();
    const chartData = [];
    
    visibleDays.forEach(day => {
      day.readings.forEach(reading => {
        let numericValue = reading.value;
        
        // Special handling for Blood Pressure (extract both systolic and diastolic)
        if (selectedVital === 'Blood Pressure') {
          const bpParts = reading.value.split('/');
          const systolic = parseFloat(bpParts[0]);
          const diastolic = parseFloat(bpParts[1]);
          
          chartData.push({
            dateTime: `${day.date} ${reading.time}`,
            date: day.date,
            time: reading.time,
            systolic: isNaN(systolic) ? null : systolic,
            diastolic: isNaN(diastolic) ? null : diastolic,
            value: isNaN(systolic) ? null : systolic, // Keep 'value' for backward compatibility
            status: reading.status
          });
        } else {
          numericValue = parseFloat(reading.value);
          
          chartData.push({
            dateTime: `${day.date} ${reading.time}`,
            date: day.date,
            time: reading.time,
            value: numericValue,
            status: reading.status
          });
        }
      });
    });
    
    return chartData;
  };

  const getVisibleDays = () => {
    if (!vitalsData[selectedVital]) return [];
    const allDays = vitalsData[selectedVital];
    return allDays.slice(currentDateIndex, currentDateIndex + 5);
  };

  const getVisibleNotes = () => {
    const visibleDays = getVisibleDays();
    const notes = [];
    visibleDays.forEach(day => {
      if (notesData[day.date]) {
        notes.push(notesData[day.date]);
      }
    });
    return notes;
  };

  const getTodayDate = () => {
    if (!vitalsData[selectedVital]) return '';
    const allDays = vitalsData[selectedVital];
    if (allDays.length === 0) return '';
    return allDays[allDays.length - 1].date;
  };

  const handlePrevDays = () => {
    if (currentDateIndex > 0) {
      setCurrentDateIndex(currentDateIndex - 1);
    }
  };

  const handleNextDays = () => {
    const maxIndex = (vitalsData[selectedVital]?.length || 0) - 5;
    if (currentDateIndex < maxIndex) {
      setCurrentDateIndex(currentDateIndex + 1);
    }
  };

  const handleAddNote = () => {
    setIsAddingNote(true);
    const todayDate = getTodayDate();
    if (notesData[todayDate]) {
      setNewNote(notesData[todayDate].note);
    } else {
      setNewNote('');
    }
  };

  const handleSaveNote = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note before saving');
      return;
    }
    
    setSavingNote(true);
    
    try {
      const todayDate = getTodayDate();
      // Convert DD/MM/YYYY to YYYY-MM-DD for backend
      const [day, month, year] = todayDate.split('/');
      const isoDate = `${year}-${month}-${day}`;
      
      // Call backend API to save notes
      await careMonitorService.addVitalsNotes(
        patient.id, // numeric patient ID
        isoDate,    // date in YYYY-MM-DD format
        newNote
      );
      
      // Update local state to reflect saved notes
      setNotesData(prev => ({
        ...prev,
        [todayDate]: {
          note: newNote,
          author: 'Admin',
          date: todayDate
        }
      }));
      
      toast.success('Note saved successfully!');
      setNewNote('');
      setIsAddingNote(false);
    } catch (error) {
      console.error('Failed to save note:', error);
      toast.error('Failed to save note. Please try again.');
    } finally {
      setSavingNote(false);
    }
  };

  const handleCancelNote = () => {
    setIsAddingNote(false);
    setNewNote('');
  };

  const range = getNormalRange(selectedVital);
  const chartData = prepareChartData();
  const visibleNotes = getVisibleNotes();

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      // Special display for Blood Pressure (show both systolic and diastolic)
      if (selectedVital === 'Blood Pressure' && data.systolic !== undefined && data.diastolic !== undefined) {
        return (
          <div className="bg-white p-3 border border-gray-300 rounded shadow-md">
            <p className="font-semibold text-gray-800">{data.dateTime}</p>
            <p className={`text-sm font-medium ${
              data.status === 'normal' ? 'text-green-600' : 
              data.status === 'warning' ? 'text-orange-600' : 
              'text-red-600'
            }`}>
              <span className="text-purple-600">Systolic: {data.systolic} mmHg</span>
              <br />
              <span className="text-blue-600">Diastolic: {data.diastolic} mmHg</span>
            </p>
            <p className="text-xs text-gray-600 capitalize">Status: {data.status}</p>
          </div>
        );
      }
      
      // Standard display for other vitals
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-md">
          <p className="font-semibold text-gray-800">{data.dateTime}</p>
          <p className={`text-sm font-medium ${
            data.status === 'normal' ? 'text-green-600' : 
            data.status === 'warning' ? 'text-orange-600' : 
            'text-red-600'
          }`}>
            Value: {data.value} {range?.unit || ''}
          </p>
          <p className="text-xs text-gray-600 capitalize">Status: {data.status}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-[95vw] h-[90vh] flex flex-col">
        {/* Compact Header */}
        <div className="bg-blue-600 text-white px-6 py-2 flex justify-between items-center rounded-t-lg">
          <div>
            <h2 className="text-lg font-bold">{patient.patient_id} - {patient.patient_name}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-blue-700 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Sidebar - Vitals Menu */}
          <div className="w-44 bg-gray-50 border-r border-gray-200 p-3 overflow-y-auto">
            <h3 className="text-xs font-semibold text-gray-700 mb-2">VITALS</h3>
            <div className="space-y-1.5">
              {vitalsMenu.map((vital) => {
                const IconComponent = vital.icon;
                return (
                  <button
                    key={vital.name}
                    onClick={() => setSelectedVital(vital.name)}
                    className={`w-full flex items-center space-x-2 px-2.5 py-2 rounded-lg transition-all text-left ${
                      selectedVital === vital.name
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <IconComponent className="h-4 w-4" />
                    <span className="text-xs font-medium">{vital.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right Content Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Compact Vital Cards Section */}
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-semibold text-gray-800">Last 5 Days - {selectedVital}</h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handlePrevDays}
                    disabled={currentDateIndex === 0}
                    className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4 text-gray-600" />
                  </button>
                  <button
                    onClick={handleNextDays}
                    disabled={currentDateIndex >= (vitalsData[selectedVital]?.length || 0) - 5}
                    className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="h-4 w-4 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Compact Cards Grid */}
              <div className="grid grid-cols-5 gap-2">
                {getVisibleDays().map((day, idx) => (
                  <div key={idx} className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-lg p-2 shadow-sm flex flex-col">
                    <div className="text-center mb-1.5">
                      <p className="text-xs font-semibold text-gray-700">{day.date}</p>
                    </div>
                    {/* Fixed height container showing max 3 readings, scrollable if more */}
                    <div className="space-y-1 overflow-y-auto max-h-[168px]" style={{ 
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#CBD5E0 #EDF2F7'
                    }}>
                      {day.readings.map((reading, rIdx) => (
                        <div key={rIdx} className={`px-1.5 py-1 rounded text-center ${getStatusColor(reading.status)}`}>
                          <p className="text-[10px] font-medium">{reading.time}</p>
                          <p className="text-xs font-bold">{reading.value}{reading.unit}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Graph and Notes Section */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
              {/* Analytics Graph */}
              <div className="bg-white rounded-lg border border-gray-200 p-3 mb-4 shadow-sm">
                <h3 className="text-base font-semibold text-gray-800 mb-2">📊 Trend Analysis - {selectedVital}</h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="dateTime" 
                        tick={{ fontSize: 9 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis tick={{ fontSize: 11 }} domain={['auto', 'auto']} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend 
                        wrapperStyle={{ fontSize: '11px' }}
                        formatter={(value) => selectedVital}
                      />
                      {range && selectedVital !== 'Blood Pressure' && (
                        <ReferenceArea 
                          y1={range.min} 
                          y2={range.max} 
                          fill="#10b981" 
                          fillOpacity={0.1} 
                          label={{ value: "Normal Range", position: "insideTopLeft", fontSize: 9, fill: "#10b981" }}
                        />
                      )}
                      
                      {/* For Blood Pressure: Show TWO lines (Systolic and Diastolic) */}
                      {selectedVital === 'Blood Pressure' ? (
                        <>
                          {/* Systolic BP Reference Area */}
                          <ReferenceArea 
                            y1={90} 
                            y2={120} 
                            fill="#a855f7" 
                            fillOpacity={0.1} 
                            label={{ value: "Systolic Normal", position: "insideTopLeft", fontSize: 9, fill: "#a855f7" }}
                          />
                          {/* Diastolic BP Reference Area */}
                          <ReferenceArea 
                            y1={60} 
                            y2={80} 
                            fill="#3b82f6" 
                            fillOpacity={0.1} 
                            label={{ value: "Diastolic Normal", position: "insideBottomLeft", fontSize: 9, fill: "#3b82f6" }}
                          />
                          {/* Systolic Line */}
                          <Line 
                            type="monotone" 
                            dataKey="systolic" 
                            stroke="#a855f7" 
                            strokeWidth={2.5}
                            dot={{ r: 4, fill: "#a855f7" }}
                            activeDot={{ r: 6 }}
                            name="Systolic BP"
                          />
                          {/* Diastolic Line */}
                          <Line 
                            type="monotone" 
                            dataKey="diastolic" 
                            stroke="#3b82f6" 
                            strokeWidth={2.5}
                            dot={{ r: 4, fill: "#3b82f6" }}
                            activeDot={{ r: 6 }}
                            name="Diastolic BP"
                          />
                        </>
                      ) : (
                        /* For other vitals: Show single line */
                        <Line 
                          type="monotone" 
                          dataKey="value" 
                          stroke={getChartColor(selectedVital)} 
                          strokeWidth={2}
                          dot={{ r: 3 }}
                          activeDot={{ r: 5 }}
                          name={selectedVital}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                {selectedVital === 'Blood Pressure' ? (
                  <div className="mt-2 text-center">
                    <p className="text-xs text-gray-600">
                      Normal Ranges: 
                      <span className="font-semibold text-purple-600 ml-1">Systolic: 90-120 mmHg</span>
                      <span className="mx-1">•</span>
                      <span className="font-semibold text-blue-600">Diastolic: 60-80 mmHg</span>
                    </p>
                  </div>
                ) : range && (
                  <div className="mt-2 text-center">
                    <p className="text-xs text-gray-600">
                      Normal Range: <span className="font-semibold text-green-600">{range.min} - {range.max} {range.unit}</span>
                    </p>
                  </div>
                )}
              </div>

              {/* Additional Notes */}
              <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-base font-semibold text-gray-800">📝 Additional Notes</h3>
                  {!isAddingNote && (
                    <button
                      onClick={handleAddNote}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs font-medium"
                    >
                      <Plus className="h-3 w-3" />
                      <span>Add Note</span>
                    </button>
                  )}
                </div>

                {/* Add/Edit Note Form */}
                {isAddingNote && (
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-800 mb-2 font-medium">
                      {notesData[getTodayDate()] ? 'Edit' : 'Add'} Note for {getTodayDate()}
                    </p>
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Enter note for today's vitals..."
                      className="w-full h-20 px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-xs"
                    />
                    <div className="mt-2 flex justify-end space-x-2">
                      <button
                        onClick={handleCancelNote}
                        className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-xs font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveNote}
                        disabled={savingNote || !newNote.trim()}
                        className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-xs font-medium"
                      >
                        <Save className="h-3 w-3" />
                        <span>{savingNote ? 'Saving...' : 'Save Note'}</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Display Notes for Visible Dates */}
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {visibleNotes.length > 0 ? (
                    visibleNotes.map((note, idx) => (
                      <div key={idx} className="p-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex items-start justify-between mb-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-semibold text-blue-600">{note.date}</span>
                            <span className="text-xs text-gray-500">•</span>
                            <span className="text-xs text-gray-600 font-medium">{note.author}</span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-700 leading-relaxed">{note.note}</p>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-xs text-gray-500">No notes available for the displayed dates</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientVitalsModal;
